import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { cachedPlacesFetch, PLACES_TTL } from "./places-http-cache.server";
import { rankDiscoveryPlaces, type DiscoveryPlace, type RankedDiscoveryPlace } from "./discovery-ranking";
import { filterToArea } from "./discovery-geo";
import { detectCuisineIntent } from "./cuisine-intent";
import { placeMatchesCuisine, requestsMarket } from "./cuisine-filter";
import { dishProven } from "./dish-evidence";
import { resolveAreaGeometry, samplingCores, insideArea } from "./area-geometry.server";
import {
  detectRestaurantsOnlyRequest,
  isVerifiedRestaurant,
  matchesRequestedLocality,
} from "./route-final-validation";

/**
 * Pesquisa de descoberta (ranking, não rota).
 *
 * NÃO consome quota de geração de itinerário: reutiliza a cache persistente de
 * Places e apenas aplica o rate limit anti-abuso já existente.
 */

const DiscoveryInput = z.object({
  searchQuery: z.string().min(2).max(120),
  specialty: z.string().max(60).nullable().optional(),
  area: z.string().max(120).nullable().optional(),
  category: z.string().max(40).nullable().optional(),
  nearMe: z.boolean().optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  language: z.string().max(8).optional(),
  /** Cozinha explícita pedida: condição vinculativa da pesquisa. */
  cuisine: z.string().max(60).nullable().optional(),
  /** Prato/produto pedido em texto livre ("bacalhau", "sushi", "gelato"). */
  dish: z.string().max(80).nullable().optional(),
  /** O produto pedido é doçaria. */
  sweetDish: z.boolean().optional(),
});

const FIELD_MASK = [
  "places.id",
  "places.displayName",
  "places.formattedAddress",
  "places.shortFormattedAddress",
  "places.location",
  "places.rating",
  "places.userRatingCount",
  "places.priceLevel",
  "places.googleMapsUri",
  "places.currentOpeningHours.openNow",
  "places.editorialSummary",
  "places.types",
  "places.primaryType",
  "places.addressComponents",
].join(",");

const PRICE_LEVELS: Record<string, number> = {
  PRICE_LEVEL_INEXPENSIVE: 1,
  PRICE_LEVEL_MODERATE: 2,
  PRICE_LEVEL_EXPENSIVE: 3,
  PRICE_LEVEL_VERY_EXPENSIVE: 4,
};

type RawPlace = {
  id?: string;
  displayName?: { text?: string };
  formattedAddress?: string;
  shortFormattedAddress?: string;
  location?: { latitude?: number; longitude?: number };
  rating?: number;
  userRatingCount?: number;
  priceLevel?: string;
  googleMapsUri?: string;
  currentOpeningHours?: { openNow?: boolean };
  editorialSummary?: { text?: string };
  types?: string[];
  primaryType?: string;
  addressComponents?: Array<{ longText?: string; types?: string[] }>;
};

function zoneOf(place: RawPlace): string | null {
  const components = place.addressComponents ?? [];
  const pick = (type: string) => components.find((c) => (c.types ?? []).includes(type))?.longText;
  return (
    pick("locality") ??
    pick("postal_town") ??
    pick("administrative_area_level_2") ??
    pick("administrative_area_level_1") ??
    null
  );
}

export type DiscoveryResult = {
  places: RankedDiscoveryPlace[];
  /** true quando não há dados suficientes para um ranking honesto. */
  insufficientData: boolean;
  /** Cozinha pedida sem locais verificados na zona: perguntar antes de alargar. */
  cuisineScarce?: { cuisine: string; area: string | null } | null;
};

export const discoverPlaces = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => DiscoveryInput.parse(d))
  .handler(async ({ data }): Promise<DiscoveryResult> => {
    const key = process.env.GOOGLE_MAPS_SERVER_API_KEY;
    if (!key) return { places: [], insufficientData: true };

    const { checkPlaceLookupRateLimit } = await import("./quota.server");
    const rl = await checkPlaceLookupRateLimit();
    if (!rl.ok) return { places: [], insufficientData: true };

    const hasOrigin =
      typeof data.lat === "number" &&
      typeof data.lng === "number" &&
      Number.isFinite(data.lat) &&
      Number.isFinite(data.lng);

    const textQuery = data.area ? `${data.searchQuery} in ${data.area}` : data.searchQuery;

    // Zona grande (região/país): amostragem de núcleos dentro dos limites
    // reais para o ranking cobrir várias localidades. Zona pequena: 1 pesquisa.
    const geometry =
      data.dish && data.area && !data.nearMe
        ? await resolveAreaGeometry(data.area, key, data.language ?? "pt")
        : null;
    const cores = geometry ? samplingCores(geometry) : [];
    const largeArea = cores.length > 1;

    const baseBody: Record<string, unknown> = {
      textQuery,
      maxResultCount: 20,
      languageCode: (data.language ?? "pt").slice(0, 2),
    };
    if (data.nearMe && hasOrigin) {
      baseBody.locationBias = {
        circle: { center: { latitude: data.lat, longitude: data.lng }, radius: 20000 },
      };
      baseBody.rankPreference = "RELEVANCE";
    }

    const bodies: Array<Record<string, unknown>> = largeArea
      ? cores.map((core) => ({
          ...baseBody,
          textQuery: data.searchQuery,
          locationBias: {
            circle: { center: { latitude: core.lat, longitude: core.lng }, radius: core.radiusMeters },
          },
          rankPreference: "RELEVANCE",
        }))
      : [baseBody];

    const responses = await Promise.all(
      bodies.map((body) =>
        cachedPlacesFetch(
          "https://places.googleapis.com/v1/places:searchText",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Goog-Api-Key": key,
              "X-Goog-FieldMask": FIELD_MASK,
            },
            body: JSON.stringify(body),
          },
          { kind: "discovery_search", ttlMs: PLACES_TTL.dining },
        ).catch(() => null),
      ),
    );
    const okResponses = responses.filter((r): r is NonNullable<typeof r> => !!r && r.ok);
    if (!okResponses.length) return { places: [], insufficientData: true };

    const payloads = await Promise.all(
      okResponses.map((r) => r.json().catch(() => ({}) as { places?: RawPlace[] })),
    );
    const merged: RawPlace[] = [];
    const seen = new Set<string>();
    for (const payload of payloads as Array<{ places?: RawPlace[] }>) {
      for (const place of payload.places ?? []) {
        if (!place.id || seen.has(place.id)) continue;
        seen.add(place.id);
        merged.push(place);
      }
    }
    const json = { places: merged };

    // Cozinha explícita: primaryType/types do Places são prova obrigatória.
    // Sem prova, o local é descartado — nunca há fallback para mercados,
    // food halls ou restaurantes genéricos.
    const cuisineIntent = data.cuisine ? detectCuisineIntent(data.cuisine) : null;
    const requestText = `${data.cuisine ?? ""} ${data.searchQuery}`;
    const allowMarket = requestsMarket(requestText);
    // Só restaurantes: nenhuma praia, igreja, atração, mercado ou food hall.
    const restaurantsOnly = !allowMarket && detectRestaurantsOnlyRequest(requestText);
    const raw: DiscoveryPlace[] = [];
    for (const p of json.places ?? []) {
      if (!p.id || !p.displayName?.text || p.location?.latitude == null || p.location.longitude == null)
        continue;
      const evidence = {
        name: p.displayName.text,
        primaryType: p.primaryType,
        types: p.types,
        summary: p.editorialSummary?.text ?? null,
      };
      // Prato pedido: prova obrigatória nos dados reais do local.
      if (data.dish && !dishProven(evidence, data.dish, { sweet: data.sweetDish })) continue;
      if (
        cuisineIntent &&
        !placeMatchesCuisine(evidence, cuisineIntent, { allowMarket })
      )
        continue;
      if (restaurantsOnly && !isVerifiedRestaurant(evidence)) continue;
      raw.push({
        placeId: p.id,
        name: p.displayName.text,
        address: p.shortFormattedAddress ?? p.formattedAddress ?? null,
        zone: zoneOf(p),
        lat: p.location.latitude,
        lng: p.location.longitude,
        rating: typeof p.rating === "number" ? p.rating : null,
        userRatingCount: typeof p.userRatingCount === "number" ? p.userRatingCount : null,
        priceLevel: p.priceLevel ? (PRICE_LEVELS[p.priceLevel] ?? null) : null,
        googleMapsUri: p.googleMapsUri ?? null,
        openNow: p.currentOpeningHours?.openNow ?? null,
        summary: p.editorialSummary?.text ?? null,
        types: p.types ?? [],
      });
    }

    // Coerência geográfica. Zona grande (região/país): vinculam os limites
    // reais do geocoder. Zona pequena: mediana dos resultados.
    let scoped = raw;
    if (data.area && !data.nearMe) {
      scoped = largeArea && geometry
        ? raw.filter((place) => insideArea(geometry, { lat: place.lat, lng: place.lng }))
        : filterToArea(raw);
    }

    // Localidade explícita vinculativa quando o pedido é só de restaurantes:
    // só passam locais cuja zona administrativa corresponda à pedida.
    // Numa zona grande a localidade administrativa NÃO é vinculativa: o que
    // vincula são os limites da região/país já aplicados acima.
    if (data.area && !data.nearMe && !largeArea && (restaurantsOnly || cuisineIntent)) {
      const inLocality = scoped.filter((place) =>
        matchesRequestedLocality(
          { name: place.name, localityNames: place.zone ? [place.zone] : [], insideRequestedArea: true },
          data.area!,
        ),
      );
      scoped = inLocality;
    }

    const ranked = rankDiscoveryPlaces(scoped, {

      specialty: data.specialty ?? null,
      category: data.category ?? null,
      nearMe: Boolean(data.nearMe),
      origin: data.nearMe && hasOrigin ? { lat: data.lat!, lng: data.lng! } : null,
      lang: (data.language ?? "pt").startsWith("pt") ? "pt" : "en",
      limit: 8,
    });

    // Com cozinha/categoria pedida mostramos todos os locais verificados; só é
    // escassez quando não existe nenhum — e aí perguntamos antes de alargar.
    const strict = Boolean(cuisineIntent || restaurantsOnly || data.dish);
    return {
      places: ranked,
      insufficientData: strict ? ranked.length === 0 : ranked.length < 3,
      cuisineScarce:
        strict && ranked.length === 0
          ? { cuisine: cuisineIntent?.requested ?? data.dish ?? "", area: data.area ?? null }
          : null,
    };
  });
